import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mb-3 row" }
const _hoisted_2 = { class: "col-sm-9" }
const _hoisted_3 = ["onClick", "aria-label"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "button-wrapper" }
const _hoisted_6 = {
  class: "btn-group",
  role: "group"
}

import {computed, onMounted, ref} from "vue";
import {BTableColumn} from "./BTable/BTable.types";
import {FormModal, InformationModal, SystemAlerts, ConfirmModal} from "./Misc";
import BGrid from "./BGrid/BGrid.vue";
import type {Vendor} from "@/types";
import {useFormValidation} from "@/validation";
import {object, string} from "yup";
import {BInput} from "./Fields";
import {BButton} from "./BButton";
import * as Yup from "yup";
import DropDown from "@/components/Misc/DropDown.vue";
import {saveFile} from "@/composable/filesHelper";
import {useConfirmDialog} from "@/composable/useConfirmDialog";
import {storeToRefs} from "pinia";
import {useVendorStore} from "@/stores/vendorStore";
import {useVendorOriStore} from "@/stores/vendorOriStore";


export default /*@__PURE__*/_defineComponent({
  __name: 'Vendors',
  setup(__props) {

const isShowEditEditor = ref(false);
const vendorStore = useVendorStore();
const vendorOriStore = useVendorOriStore();
const {confirm} = useConfirmDialog();
const {loading} = storeToRefs(vendorStore);
const vendorAgencyTitle = ref("");
const isShowVendorAgency = ref(false);

const columns = ref<BTableColumn[]>([
    {
        key: "name",
        text: "Vendor name",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.name,
    },

    {
        key: "agencyCount",
        text: "Number of Agencies",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.agencyCount,
    },

    {
        key: "actions",
        text: "",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap text-end"],
        value: (item) => item["link"],
    },
]);

const columnsVendorAgency = ref<BTableColumn[]>([
    {
        key: "ori",
        text: "ORI",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.ori,
    },

    {
        key: "oriName",
        text: "Agency Name",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.oriName,
    },
    {
        key: "dataCollection",
        text: "Data Collection(s)",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.collections,
    },
]);

const canAdd = computed(() => {
    return true;
});
const canDelete = (item: Vendor) => {
    return item.agencyCount == 0 && item.code != "UV";
};
const deleteButtonTooltip = (item: Vendor) => {
    if (item.code == "UV") {
        return "Cannot delete: this is the default vendor";
    }
    if (item.agencyCount > 0) {
        return `Cannot delete: vendor is linked to ${item.agencyCount} ${item.agencyCount == 1 ? "agency" : "agencies"}`;
    }
    return "Delete vendor";
};
const canEdit = computed(() => {
    return true;
});

const editDialogTitle = computed(() => {
    return selectedVendorId.value ? `Edit vendor - ${vendor.value.name}`:"Add new vendor";
});

const canEditCode = computed(() => {
    return !selectedVendorId.value;
});

const vendor = ref<Vendor>({} as Vendor);
const selectedVendorId = ref<string>("");

Yup.addMethod(Yup.string, 'uniqueVendorName', function (errorMessage: string) {
    return this.test('unique-vendor-name', errorMessage, async function (value) {
        const { path, createError } = this;

        var vendorAlreadyExists = value != undefined ? (await vendorStore.checkIfVendorNameExists(value)): true;
        return vendorAlreadyExists 
            ? createError({ path, message: errorMessage }) 
            : true;
    });
});

const schema = object<Vendor>({    
    name: string().nullable().trim().label("Vendor name").max(100).required().uniqueVendorName("Vendor Name already exists!"),
});

const createVendor = async () => {
    if (await handleSubmit(schema, vendor.value)) {
        if (selectedVendorId.value == "") {
            await vendorStore.create(vendor.value);
        } else {
            await vendorStore.update(selectedVendorId.value, vendor.value);
        }

        isShowEditEditor.value = false;
        await getData();
    }
};

const exportData = async (type: string) => {
    let blob = await vendorStore.exportFile(type);
    saveFile(`vendors.${type}`, blob);
};

const deleteItem = async (code: string) => {
    const shouldProceed = await confirm("Delete Confirmation", "Are You Sure?");
    if (shouldProceed) {
        await vendorStore.deleteItem(code);
        await getData();
    }
};

const showVendorAgency = async (vendor: Vendor) => {
    vendorOriStore.reset();
    isShowVendorAgency.value = true;
    vendorAgencyTitle.value = `Agencies for ${vendor.name}`;
    selectedVendorId.value = vendor.code;
    getAgenciesData();
};

const showEditEditor = async (vendorId: string) => {
    selectedVendorId.value = vendorId;
    resetValidation();

    if (vendorId == "") {
        vendor.value = {} as Vendor;
    } else {
        vendor.value = await vendorStore.getById(vendorId);
    }
    isShowEditEditor.value = true;
};

const getData = async () => {
    await vendorStore.list();
};

const getAgenciesData = async () => {
    await vendorOriStore.list(selectedVendorId.value);
};

const {handleSubmit, errors, resetValidation} = useFormValidation();

onMounted(async () => {
    await getData();
});

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(InformationModal), {
      title: vendorAgencyTitle.value,
      modelValue: isShowVendorAgency.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isShowVendorAgency).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(BGrid, {
          caption: "Vendors agenices",
          tableOptions: _unref(vendorOriStore).tableOptions,
          filterableFields: _unref(vendorOriStore).filterableFields,
          filterOptions: _unref(vendorOriStore).filterOptions,
          onOptionChange: getAgenciesData,
          items: _unref(vendorOriStore).items,
          columns: columnsVendorAgency.value,
          loading: _unref(loading)
        }, null, 8, ["tableOptions", "filterableFields", "filterOptions", "items", "columns", "loading"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_unref(FormModal), {
      title: editDialogTitle.value,
      modelValue: isShowEditEditor.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isShowEditEditor).value = $event)),
      loading: _unref(loading),
      onSubmit: createVendor
    }, {
      default: _withCtx(() => [
        _createElementVNode("fieldset", null, [
          _createElementVNode("div", _hoisted_1, [
            _cache[6] || (_cache[6] = _createElementVNode("label", {
              for: "vendorName",
              class: "col-sm-3 col-form-label"
            }, " Vendor Name: ", -1)),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(BInput), {
                name: "vendorName",
                type: "text",
                "max-length": 100,
                modelValue: vendor.value.name,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((vendor.value.name) = $event)),
                error: _unref(errors).name
              }, null, 8, ["modelValue", "error"])
            ])
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "modelValue", "loading"]),
    _createVNode(BGrid, {
      caption: "Vendors",
      tableOptions: _unref(vendorStore).tableOptions,
      filterableFields: _unref(vendorStore).filterableFields,
      filterOptions: _unref(vendorStore).filterOptions,
      onOptionChange: getData,
      items: _unref(vendorStore).items,
      columns: columns.value,
      loading: _unref(loading)
    }, {
      beforeFilter: _withCtx(() => [
        (canAdd.value)
          ? (_openBlock(), _createBlock(_unref(BButton), {
              key: 0,
              onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (showEditEditor('')), ["prevent"]))
            }, {
              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                _createTextVNode("Add New Vendor", -1)
              ]))]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      "cell.agencyCount": _withCtx(({item}) => [
        (item.agencyCount > 0)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-link p-0",
              onClick: _withModifiers(($event: any) => (showVendorAgency(item)), ["prevent"]),
              "aria-label": `View ${item.agencyCount} ${item.agencyCount === 1 ? 'agency' : 'agencies'} for vendor ${item.name}`
            }, _toDisplayString(item.agencyCount), 9, _hoisted_3))
          : (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(item.agencyCount), 1))
      ]),
      "cell.actions": _withCtx(({item}) => [
        _createElementVNode("div", _hoisted_5, [
          (canEdit.value)
            ? (_openBlock(), _createBlock(_unref(BButton), {
                key: 0,
                onClick: _withModifiers(($event: any) => (showEditEditor(item.code)), ["prevent"]),
                "aria-label": `Edit vendor ${item.name}`,
                size: "sm"
              }, {
                default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                  _createTextVNode(" Edit ", -1)
                ]))]),
                _: 1
              }, 8, ["onClick", "aria-label"]))
            : _createCommentVNode("", true),
          _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
            onClick: _withModifiers(($event: any) => (deleteItem(item.code)), ["prevent"]),
            disabled: !canDelete(item),
            "aria-label": `Delete vendor ${item.name}. ${deleteButtonTooltip(item)}`,
            size: "sm",
            variant: "danger"
          }, {
            default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
              _createTextVNode(" Delete ", -1)
            ]))]),
            _: 1
          }, 8, ["onClick", "disabled", "aria-label"])), [
            [_directive_tooltip, deleteButtonTooltip(item)]
          ])
        ])
      ]),
      pagingStart: _withCtx(() => [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(DropDown, {
            label: "Export Data",
            "item-count": 2,
            "is-primary-button": true
          }, {
            item1: _withCtx(({item}) => [
              _createElementVNode("button", {
                class: "dropdown-item",
                onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (exportData('csv')), ["prevent"]))
              }, " Download as .csv ")
            ]),
            item2: _withCtx(({item}) => [
              _createElementVNode("button", {
                class: "dropdown-item",
                onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (exportData('xlsx')), ["prevent"]))
              }, " Download as .xlsx ")
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["tableOptions", "filterableFields", "filterOptions", "items", "columns", "loading"]),
    _createVNode(_unref(SystemAlerts)),
    _createVNode(_unref(ConfirmModal))
  ], 64))
}
}

})